import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Table,
  Spinner,
  Alert,
  Badge,
} from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import {
  FaBed,
  FaFileExcel,
  FaSearch,
  FaSync,
} from "react-icons/fa";
import pythonApiClient from "../../../services/api/pythonBaseApi";
import "react-toastify/dist/ReactToastify.css";

const hoyISO = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const badgeFor = (codigo) => {
  if (codigo === "HABILITADO") return "warning";
  if (codigo === "SABANAS") return "info";
  return "success";
};

const HousekeepingDiario = () => {
  const selectedProject = JSON.parse(
    localStorage.getItem("selectedProject") || "null"
  );

  const [filtros, setFiltros] = useState({
    fecha: hoyISO(),
    tipo_tarea: "",
  });
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [error, setError] = useState(null);
  const [resultado, setResultado] = useState(null);

  const params = useMemo(() => {
    const p = { fecha: filtros.fecha };
    if (filtros.tipo_tarea) p.tipo_tarea = filtros.tipo_tarea;
    return p;
  }, [filtros]);

  const consultar = async () => {
    if (!selectedProject?.id) {
      toast.error("Seleccione un proyecto");
      return;
    }
    if (!filtros.fecha) {
      toast.error("Indique la fecha del plan");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await pythonApiClient.get(
        "/procesos/hoteleria/housekeeping/diario",
        {
          params,
          headers: { "X-Proyecto-ID": selectedProject.id },
          timeout: 120000,
        }
      );
      setResultado(response.data);
      toast.success(
        `Plan generado: ${response.data?.detalles_count ?? 0} tareas`
      );
    } catch (err) {
      console.error(err);
      const msg =
        err.response?.data?.detail ||
        err.message ||
        "No se pudo generar el plan";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const descargarExcel = async () => {
    if (!selectedProject?.id) {
      toast.error("Seleccione un proyecto");
      return;
    }
    setExportando(true);
    try {
      const response = await pythonApiClient.get(
        "/procesos/hoteleria/housekeeping/diario/excel",
        {
          params,
          headers: { "X-Proyecto-ID": selectedProject.id },
          responseType: "blob",
          timeout: 180000,
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `housekeeping_${filtros.fecha || hoyISO()}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel descargado");
    } catch (err) {
      console.error(err);
      toast.error(
        err.response?.data?.detail || err.message || "Error al exportar"
      );
    } finally {
      setExportando(false);
    }
  };

  useEffect(() => {
    if (selectedProject?.id) {
      consultar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject?.id]);

  const totales = resultado?.totales || {};
  const resumen = resultado?.resumen_modulos || [];
  const detalles = resultado?.detalles || [];

  return (
    <div className="dh-reporte-bg">
      <style>{`
        .dh-reporte-bg {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          min-height: 100vh;
          padding-bottom: 2rem;
        }
        .dh-main-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 2rem 0;
          margin-bottom: 2rem;
          border-radius: 0 0 30px 30px;
        }
        .dh-main-title { font-size: 2rem; font-weight: 700; margin: 0; }
        .dh-main-subtitle { opacity: 0.9; margin-top: 0.5rem; }
        .dh-card {
          border: none; border-radius: 20px;
          box-shadow: 0 15px 35px rgba(0,0,0,0.1);
        }
        .dh-card-header {
          border-radius: 20px 20px 0 0; padding: 1.25rem 1.5rem; border: none;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white; font-weight: 700;
        }
        .btn-dh-primary {
          background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
          border: none; color: white; border-radius: 12px; padding: 10px 18px; font-weight: 600;
        }
        .btn-dh-secondary {
          background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
          border: none; color: white; border-radius: 12px; padding: 10px 18px; font-weight: 600;
        }
        .dh-form-control { border-radius: 10px; }
        .dh-alert-info {
          background: linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%);
          border: 1px solid #93c5fd; border-radius: 12px;
          padding: 1rem 1.25rem; color: #1e40af;
        }
        .stat-box {
          background: white; border-radius: 16px; padding: 1.25rem;
          box-shadow: 0 8px 20px rgba(0,0,0,0.06); text-align: center;
        }
        .stat-box h4 { margin: 0; font-weight: 800; color: #374151; }
        .stat-box small { color: #6b7280; }
      `}</style>

      <div className="dh-main-header">
        <Container>
          <h1 className="dh-main-title">
            <FaBed className="me-2" />
            Housekeeping Diario
          </h1>
          <p className="dh-main-subtitle">
            Plan operativo por check-in (régimen 21×7): sábanas D7/D14, habilitado
            D21, tendido el resto
          </p>
        </Container>
      </div>

      <Container>
        <Alert className="dh-alert dh-alert-info mb-4">
          Check-in = día 1. El ciclo no se reinicia al cambiar de cama
          (`fecha_inicio_ciclo_hk`). Un checkout anticipado genera{" "}
          <strong>Habilitado Completo</strong> ese día.
        </Alert>

        <Card className="dh-card mb-4">
          <Card.Header className="dh-card-header">Filtros</Card.Header>
          <Card.Body>
            <Row className="g-3 align-items-end">
              <Col md={3}>
                <Form.Label className="fw-bold">Fecha del plan</Form.Label>
                <Form.Control
                  type="date"
                  className="dh-form-control"
                  value={filtros.fecha}
                  onChange={(e) =>
                    setFiltros((p) => ({ ...p, fecha: e.target.value }))
                  }
                />
              </Col>
              <Col md={3}>
                <Form.Label className="fw-bold">Tipo de tarea</Form.Label>
                <Form.Select
                  className="dh-form-control"
                  value={filtros.tipo_tarea}
                  onChange={(e) =>
                    setFiltros((p) => ({ ...p, tipo_tarea: e.target.value }))
                  }
                >
                  <option value="">Todas</option>
                  <option value="HABILITADO">Habilitado Completo</option>
                  <option value="SABANAS">Cambio de Sábanas</option>
                  <option value="TENDIDO">Limpieza / Tendido</option>
                </Form.Select>
              </Col>
              <Col md={6} className="d-flex flex-wrap gap-2">
                <Button
                  className="btn-dh-primary"
                  onClick={consultar}
                  disabled={loading || exportando}
                >
                  {loading ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <FaSearch className="me-2" />
                  )}
                  Consultar
                </Button>
                <Button
                  className="btn-dh-secondary"
                  onClick={descargarExcel}
                  disabled={loading || exportando}
                >
                  {exportando ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <FaFileExcel className="me-2" />
                  )}
                  Exportar Excel
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={consultar}
                  disabled={loading}
                  title="Actualizar"
                >
                  <FaSync />
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {error && (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        )}

        {resultado && (
          <>
            <Row className="g-3 mb-4">
              <Col xs={6} md={3}>
                <div className="stat-box">
                  <h4>{totales.HABILITADO ?? 0}</h4>
                  <small>Habilitado Completo</small>
                </div>
              </Col>
              <Col xs={6} md={3}>
                <div className="stat-box">
                  <h4>{totales.SABANAS ?? 0}</h4>
                  <small>Cambio de Sábanas</small>
                </div>
              </Col>
              <Col xs={6} md={3}>
                <div className="stat-box">
                  <h4>{totales.TENDIDO ?? 0}</h4>
                  <small>Limpieza / Tendido</small>
                </div>
              </Col>
              <Col xs={6} md={3}>
                <div className="stat-box">
                  <h4>{totales.TOTAL ?? 0}</h4>
                  <small>Total camas</small>
                </div>
              </Col>
            </Row>

            <Card className="dh-card mb-4">
              <Card.Header className="dh-card-header">
                Resumen por módulo
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead>
                      <tr>
                        <th>Módulo / Bloque</th>
                        <th className="text-center">Habilitado</th>
                        <th className="text-center">Sábanas</th>
                        <th className="text-center">Tendido</th>
                        <th className="text-center">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resumen.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="text-center text-muted py-4">
                            Sin datos para la fecha
                          </td>
                        </tr>
                      ) : (
                        resumen.map((r) => (
                          <tr key={r.modulo_id || r.modulo}>
                            <td>{r.modulo}</td>
                            <td className="text-center">{r.HABILITADO ?? 0}</td>
                            <td className="text-center">{r.SABANAS ?? 0}</td>
                            <td className="text-center">{r.TENDIDO ?? 0}</td>
                            <td className="text-center fw-semibold">
                              {r.TOTAL ?? 0}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>

            <Card className="dh-card mb-4">
              <Card.Header className="dh-card-header">
                Detalle operativo ({detalles.length})
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive" style={{ maxHeight: 560 }}>
                  <Table hover size="sm" className="mb-0">
                    <thead style={{ position: "sticky", top: 0, background: "#fff" }}>
                      <tr>
                        <th>DNI</th>
                        <th>Trabajador</th>
                        <th>Módulo</th>
                        <th>Hab - Cama</th>
                        <th>Check-in</th>
                        <th className="text-center">Días</th>
                        <th>Tarea</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detalles.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-center text-muted py-4">
                            No hay tareas para los filtros seleccionados
                          </td>
                        </tr>
                      ) : (
                        detalles.map((d) => (
                          <tr key={`${d.asignacion_id}-${d.tipo_tarea_codigo}`}>
                            <td>{d.dni}</td>
                            <td>{d.trabajador}</td>
                            <td>{d.modulo}</td>
                            <td>{d.hab_cama}</td>
                            <td>{d.fecha_check_in}</td>
                            <td className="text-center">{d.dias_pernocte}</td>
                            <td>
                              <Badge bg={badgeFor(d.tipo_tarea_codigo)}>
                                {d.tipo_tarea}
                              </Badge>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </>
        )}
      </Container>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default HousekeepingDiario;
